import {
  PromiseResourceOptions,
  resource,
  Resource,
  ResourceRef,
  ResourceStatus,
  signal,
  WritableResource,
} from '@angular/core';
import { of, throwError } from 'rxjs';
import { CancelledError } from '../types/errors';
import { rxResource, RxResourceOptions } from '@angular/core/rxjs-interop';

export interface Cancelable {
  cancel(): void;
}

export type CancelableResourceRef<T> = Cancelable & ResourceRef<T>;

export type ReadonlyCancelableResourceRef<T> = Cancelable &
  Resource<T> & { reload: ResourceRef<T>['reload'] };

export interface CancelableRxResourceOptions<T, R> extends RxResourceOptions<T, R> {
  skipInitialLoad?: boolean;
}

export interface CancelableResourceOptions<T, R> extends PromiseResourceOptions<T, R> {
  skipInitialLoad?: boolean;
}

export function cancelableResource<T, R>(
  options: CancelableResourceOptions<T, R>
): CancelableResourceRef<T | undefined> {
  const cancelSignal = signal(false);
  const ref = resource({
    params: () => ({
      params: (options.params ? options.params() : null) as Exclude<
        NoInfer<R>,
        undefined
      >,
      cancel: cancelSignal(),
    }),
    loader: async (param) => {
      if (options.skipInitialLoad && param.previous.status === 'idle') return undefined;
      if (param.params.cancel) throw new CancelledError();
      return await options.loader({
        abortSignal: param.abortSignal,
        params: param.params.params,
        previous: param.previous,
      });
    },
  });
  const returnValue = {
    ...ref,
    cancel: () => {
      cancelSignal.set(true);
    },
    reload: () => {
      if (cancelSignal()) {
        cancelSignal.set(false);
        return true;
      } else return ref.reload();
    },
  };
  const readonlyValue = {
    ...ref.asReadonly(),
    cancel: () => {
      cancelSignal.set(true);
    },
    reload: () => {
      if (cancelSignal()) {
        cancelSignal.set(false);
        return true;
      } else return ref.reload();
    },
  };
  return {
    ...returnValue,
    asReadonly: () => {
      return readonlyValue;
    },
  };
}

export function cancelableRxResource<T, R>(
  options: CancelableRxResourceOptions<T, R>
): CancelableResourceRef<T | undefined> {
  const cancelSignal = signal(false);
  const ref = rxResource({
    params: () => ({
      params: (options.params ? options.params() : null) as Exclude<
        NoInfer<R>,
        undefined
      >,
      cancel: cancelSignal(),
    }),
    stream: (param) => {
      if (options.skipInitialLoad && param.previous.status === 'idle')
        return of(undefined as T);
      if (param.params.cancel) return throwError(() => new CancelledError());
      return options.stream({
        abortSignal: param.abortSignal,
        params: param.params.params,
        previous: param.previous,
      });
    },
  });
  const returnValue = {
    ...ref,
    cancel: () => {
      cancelSignal.set(true);
    },
    reload: () => {
      if (cancelSignal()) {
        cancelSignal.set(false);
        return true;
      } else return ref.reload();
    },
  };
  const readonlyValue = {
    ...ref.asReadonly(),
    cancel: () => {
      cancelSignal.set(true);
    },
    reload: () => {
      if (cancelSignal()) {
        cancelSignal.set(false);
        return true;
      } else return ref.reload();
    },
  };
  return {
    ...returnValue,
    asReadonly: () => {
      return readonlyValue;
    },
  };
}
